/**
 * Helpers de rapprochement entre un document et son process de signature e-sign.
 *
 * Les events de communication e-sign (state `messagingModule.emailEvents`) portent, dans leur
 * `businessdata`, la liste des documents de l'enveloppe sous la clé `listDocuments`, propagée
 * depuis `signingFile` (orchestration) → businessData du process (odm-esign) → paramètres du mail
 * → `_business_data` du `contactmechanism_communication_event`.
 *
 * L'event d'invitation porte également, au même niveau, le lien de signature décomposé en deux
 * paramètres : `front_end_esign_url` (base) et `process_token` (jeton du process/signataire).
 *
 * L'emplacement exact de ces clés peut varier selon la sérialisation (niveau racine, ou imbriqué
 * sous `business.data`). On les recherche donc en profondeur pour rester robuste.
 */

/** Clé portant la liste des documents de l'enveloppe dans le businessData des events. */
const LIST_DOCUMENTS_KEY = "listDocuments";

/** Clé de la base d'URL du front e-sign. */
const FRONT_END_ESIGN_URL_KEY = "front_end_esign_url";

/** Clé du jeton de process/signataire à appondre à la base d'URL. */
const PROCESS_TOKEN_KEY = "process_token";

/** Profondeur maximale d'exploration du businessData (garde-fou contre les cycles/objets profonds). */
const MAX_DEPTH = 8;

/**
 * Parse une valeur potentiellement stockée en chaîne JSON (colonne `_business_data`), sinon la
 * retourne telle quelle.
 */
function parseMaybeJson(value: any): any {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

/** Retourne le businessData d'un event (parsé si stocké en chaîne JSON). */
function eventBusinessData(event: any): any {
  return parseMaybeJson(event?.businessdata);
}

/**
 * Collecte récursivement tous les tableaux `listDocuments` présents dans un noeud du businessData,
 * quel que soit leur niveau d'imbrication.
 */
function collectListDocumentsDeep(node: any, acc: any[][], depth = 0): void {
  if (!node || typeof node !== "object" || depth > MAX_DEPTH) {
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item) => collectListDocumentsDeep(item, acc, depth + 1));
    return;
  }
  Object.entries(node).forEach(([key, rawValue]) => {
    if (key === LIST_DOCUMENTS_KEY && Array.isArray(rawValue)) {
      acc.push(rawValue);
    }
    // Certaines valeurs imbriquées (ex : "business.data") peuvent elles-mêmes être des chaînes JSON.
    const child = typeof rawValue === "string" ? parseMaybeJson(rawValue) : rawValue;
    if (child && typeof child === "object") {
      collectListDocumentsDeep(child, acc, depth + 1);
    }
  });
}

/** Vrai si la valeur est renseignée (ni undefined, ni null, ni chaîne vide). */
function isPresent(value: any): boolean {
  return value !== undefined && value !== null && value !== "";
}

/**
 * Recherche en profondeur la première valeur renseignée associée à `targetKey` dans un noeud du
 * businessData (gère les valeurs imbriquées éventuellement stockées en chaîne JSON).
 */
function findFirstDeep(node: any, targetKey: string, depth = 0): any {
  if (!node || typeof node !== "object" || depth > MAX_DEPTH) {
    return undefined;
  }
  const entries: Array<[any, any]> = Array.isArray(node)
    ? node.map((item) => [null, item])
    : Object.entries(node);
  for (const [key, rawValue] of entries) {
    if (key === targetKey && isPresent(rawValue)) {
      return rawValue;
    }
    const child = typeof rawValue === "string" ? parseMaybeJson(rawValue) : rawValue;
    if (child && typeof child === "object") {
      const found = findFirstDeep(child, targetKey, depth + 1);
      if (isPresent(found)) {
        return found;
      }
    }
  }
  return undefined;
}

/** Extrait l'ensemble des `receivedFilesUid` rattachés au(x) `listDocuments` d'un seul event. */
function eventReceivedFileUids(event: any): Set<string> {
  const lists: any[][] = [];
  collectListDocumentsDeep(eventBusinessData(event), lists);
  const uids = new Set<string>();
  lists.forEach((list) =>
    list.forEach((doc) => {
      const uid = doc?.receivedFilesUid;
      if (uid) {
        uids.add(String(uid));
      }
    })
  );
  return uids;
}

/**
 * Extrait, depuis la liste des `emailEvents`, l'ensemble des `receivedFilesUid` rattachés à un
 * process de signature (via les `listDocuments` de leur businessData).
 */
export function collectSignatureReceivedFileUids(emailEvents: any[]): Set<string> {
  const uids = new Set<string>();
  (emailEvents || []).forEach((event) => {
    eventReceivedFileUids(event).forEach((uid) => uids.add(uid));
  });
  return uids;
}

/**
 * Vrai si le document (via son `receivedFilesUid`) est rattaché à un process de signature présent
 * dans les `emailEvents`.
 */
export function documentHasSignatureProcess(document: any, emailEvents: any[]): boolean {
  const uid = document?.receivedFilesUid;
  if (!uid) {
    return false;
  }
  return collectSignatureReceivedFileUids(emailEvents).has(String(uid));
}

/**
 * Construit le lien de signature d'un event selon le pattern exact `front_end_esign_url/process_token`
 * (une seule "/" de séparation, la base pouvant déjà se terminer par "/"). Retourne `null` si l'un
 * des deux paramètres est absent (ex : mail de confirmation/refus sans jeton).
 */
export function buildSignatureUrlFromEvent(event: any): string | null {
  const businessData = eventBusinessData(event);
  const base = findFirstDeep(businessData, FRONT_END_ESIGN_URL_KEY);
  const token = findFirstDeep(businessData, PROCESS_TOKEN_KEY);
  if (!base || !token) {
    return null;
  }
  return `${String(base).replace(/\/+$/, "")}/${String(token)}`;
}

/**
 * Construit l'URL de signature pour un document donné : sélectionne le premier event dont les
 * `listDocuments` contiennent le `receivedFilesUid` du document ET qui porte un lien exploitable
 * (`front_end_esign_url` + `process_token`), puis en dérive l'URL.
 */
export function getSignatureUrlForDocument(document: any, emailEvents: any[]): string | null {
  const uid = document?.receivedFilesUid;
  if (!uid) {
    return null;
  }
  for (const event of emailEvents || []) {
    if (!eventReceivedFileUids(event).has(String(uid))) {
      continue;
    }
    const url = buildSignatureUrlFromEvent(event);
    if (url) {
      return url;
    }
  }
  return null;
}
